import React, { useState, useEffect } from 'react';
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Popover, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { Calendar } from 'lucide-react';
import { format, setMonth, setYear, getYear } from 'date-fns';
import { id } from 'date-fns/locale';
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";

const parseValueToDate = (val) => {
  if (!val) return null;
  if (val instanceof Date) return isNaN(val.getTime()) ? null : val;
  if (typeof val === 'string') {
    const trimmed = val.trim();
    if (trimmed.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [y, m, d] = trimmed.split('-').map(Number);
      return new Date(y, m - 1, d);
    }
    const months = { 'jan': 0, 'feb': 1, 'mar': 2, 'apr': 3, 'mei': 4, 'jun': 5, 'jul': 6, 'agu': 7, 'sep': 8, 'okt': 9, 'nov': 10, 'des': 11 };
    const parts = trimmed.split(/\s+/);
    if (parts.length >= 3) {
      const day = parseInt(parts[0], 10);
      const monthKey = parts[1].toLowerCase().substring(0, 3);
      const year = parseInt(parts[2], 10);
      if (!isNaN(day) && monthKey in months && !isNaN(year)) {
        return new Date(year, months[monthKey], day);
      }
    }
    const parsed = new Date(val);
    if (!isNaN(parsed.getTime())) return parsed;
  }
  return null;
};

export default function CustomDatePicker({ 
  value, 
  onChange, 
  placeholder = "Pilih tanggal",
  className = "",
  fromYear,
  toYear,
  align = "start"
}) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState('date'); // 'date' | 'month' | 'year'
  const parsedDate = parseValueToDate(value);
  const [currentDate, setCurrentDate] = useState(parsedDate || new Date());

  useEffect(() => {
    if (open) {
      const p = parseValueToDate(value);
      if (p) setCurrentDate(p);
      setView('date');
    }
  }, [open, value]);

  const handleMonthSelect = (monthIdx) => {
    setCurrentDate(setMonth(currentDate, monthIdx));
    setView('date');
  };

  const handleYearSelect = (year) => {
    setCurrentDate(setYear(currentDate, year));
    setView('month');
  };

  const startY = fromYear !== undefined ? fromYear : getYear(new Date()) - 80;
  const endY = toYear !== undefined ? toYear : getYear(new Date()) + 10;
  const years = Array.from({ length: Math.max(1, endY - startY + 1) }, (_, i) => endY - i); // Descending
  const months = Array.from({ length: 12 }, (_, i) => setMonth(new Date(), i));

  // Custom Caption that replaces the Month Year text with buttons
  const CustomCaptionLabel = ({ displayMonth }) => {
    return (
      <div className="flex gap-1 items-center relative z-10 pointer-events-auto">
         <Button type="button" variant="ghost" size="sm" onClick={() => setView('month')} className="h-7 px-2 text-sm font-bold bg-slate-100/50 hover:bg-slate-200 capitalize">
           {format(displayMonth, 'MMMM', { locale: id })}
         </Button>
         <Button type="button" variant="ghost" size="sm" onClick={() => setView('year')} className="h-7 px-2 text-sm font-bold bg-slate-100/50 hover:bg-slate-200">
           {format(displayMonth, 'yyyy', { locale: id })}
         </Button>
      </div>
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <Button 
          variant="outline" 
          className={`w-full justify-start text-left font-normal h-9 text-xs border-slate-200 ${className} ${!value ? 'text-slate-400' : 'text-slate-700'}`}
        >
          <Calendar className="mr-2 h-3.5 w-3.5 shrink-0" />
          {parsedDate ? format(parsedDate, 'd MMM yyyy', { locale: id }) : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      {/* We render PopoverContent inline without Portal to prevent click block issues in custom modals */}
      <PopoverPrimitive.Content
        align={align}
        sideOffset={4}
        className={cn(
          "z-[99999] w-auto rounded-md border bg-popover p-0 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
          "pointer-events-auto"
        )}
      >
        {view === 'year' && (
          <div className="p-3 w-[260px] md:w-[280px]">
            <div className="flex items-center justify-between mb-2 pb-2 border-b">
              <span className="text-sm font-bold px-2">Pilih Tahun</span>
              <Button variant="ghost" size="sm" className="h-7 px-3 text-xs" onClick={() => setView('date')}>Batal</Button>
            </div>
            <ScrollArea className="h-[220px]">
              <div className="grid grid-cols-3 gap-2 pr-3">
                {years.map(y => (
                  <Button 
                    key={y} 
                    variant={getYear(currentDate) === y ? "default" : "outline"} 
                    className={cn("h-9 text-sm", getYear(currentDate) === y ? "bg-[#8C6208] text-white hover:bg-[#6D4C06]" : "hover:bg-slate-100")}
                    onClick={() => handleYearSelect(y)}
                  >
                    {y}
                  </Button>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}

        {view === 'month' && (
          <div className="p-3 w-[260px] md:w-[280px]">
            <div className="flex items-center justify-between mb-2 pb-2 border-b">
              <span className="text-sm font-bold px-2">Pilih Bulan</span>
              <Button variant="ghost" size="sm" className="h-7 px-3 text-xs" onClick={() => setView('date')}>Batal</Button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {months.map((m, i) => (
                <Button 
                  key={i} 
                  variant={currentDate.getMonth() === i ? "default" : "outline"} 
                  className={cn("h-10 text-xs capitalize", currentDate.getMonth() === i ? "bg-[#8C6208] text-white hover:bg-[#6D4C06]" : "hover:bg-slate-100")}
                  onClick={() => handleMonthSelect(i)}
                >
                  {format(m, 'MMM', { locale: id })}
                </Button>
              ))}
            </div>
          </div>
        )}

        <div className={view === 'date' ? 'block' : 'hidden'}>
          <CalendarComponent
            mode="single"
            selected={parsedDate || undefined}
            onSelect={(date) => {
              onChange(date ? format(date, 'yyyy-MM-dd') : '');
              setOpen(false);
            }}
            month={currentDate}
            onMonthChange={setCurrentDate}
            initialFocus
            locale={id}
            fromYear={fromYear}
            toYear={toYear}
            components={{
              CaptionLabel: CustomCaptionLabel
            }}
          />
        </div>
      </PopoverPrimitive.Content>
    </Popover>
  );
}
